import {makeAutoObservable} from 'mobx';
import {toast} from 'react-toastify';
import LoadingModule from '../../../../modules/LoadingModule/LoadingModule';
import MicroreelsRepository from '../../../../data/repositories/MicroreelsRepository';
import CoursesRepository from '../../../../data/repositories/CoursesRepository';
import {AIRepository} from '../../../../data/repositories/AiRepository';
import MicrolessonsRepository from '../../../../data/repositories/MicrolessonsRepository';
import LibraryRepository from '../../../../data/repositories/LibraryRepository';
import {MediaFile} from '../../../../data/models/LibraryFile/MediaFile';
import {VideoCostEstimationModel} from '../../../../data/models/AI/VideoGenerationConfig';
import {
  MicroreelGenerationModel,
  MicroreelSlideGenerationModel,
  MicroreelVideoGenerationModel,
  MicroreelsGenerationStatus,
  MicroreelsGenerationStatusResult,
} from '../../../../data/models/Microreels/MicroreelGenerationModel';
import {getErrorFromCode, serverErrorCodes} from '../../../../data/errorCodes';
import {toastConfig} from '../../../../uiToolkit/Toast/toastConfig';
import {generateMicroreelText} from '../generateMicroreelText';
import {
  readGenerationInfos,
  removeGenerationInfo,
  storeGenerationResult,
  storeGroupSettings,
  StoredGenerationInfo,
} from '../../../Microreels/Generating/generatingTypes';
import {
  GenerationMatchResult,
  matchGenerations,
} from '../../../Microreels/Generating/generationTracker';

const SETTLE_PAGE_SIZE = 10;

export interface GenerationStartResult {
  started: boolean;
  // Job id from the generate response. Absent when the response was lost.
  generationId?: string;
}

// The backend stores the group name truncated to 40 chars and enforces no
// uniqueness; the tracker matches finished generations back to their group
// by title, so duplicates would cross-match.
const TITLE_CHECK_PAGE_SIZE = 50;
const MAX_GROUP_TITLE_LENGTH = 40;
const normalizeGroupTitle = (title?: string) =>
  (title ?? '').trim().slice(0, MAX_GROUP_TITLE_LENGTH).trim().toLowerCase();
// The base is shortened so the suffix survives the backend's truncation.
const suffixedGroupTitle = (base: string, n: number) => {
  const suffix = ` ${n}`;
  return `${base
    .slice(0, MAX_GROUP_TITLE_LENGTH - suffix.length)
    .trim()}${suffix}`;
};

class GenerateMicroreelStore {
  private loadingModule = new LoadingModule();
  private microreelsRepo = new MicroreelsRepository();
  private coursesRepo = new CoursesRepository();
  private aiRepo = new AIRepository();
  private microlessonsRepo = new MicrolessonsRepository();
  private libraryRepo = new LibraryRepository();

  constructor() {
    makeAutoObservable(this);
  }

  public get isLoading() {
    return this.loadingModule.isLoading;
  }

  public async generateFromPrompt(
    organizationId: string,
    model: MicroreelGenerationModel,
  ) {
    this.loadingModule.startLoading();
    try {
      if (await this.rejectTakenTitle(organizationId, model.name)) {
        return {started: false};
      }
      const result = await this.microreelsRepo.generateFromPrompt(
        organizationId,
        model,
      );
      return this.resolveGenerationResult(result);
    } finally {
      this.loadingModule.endLoading();
    }
  }

  public async generateFromSlide(
    organizationId: string,
    model: MicroreelSlideGenerationModel,
  ) {
    this.loadingModule.startLoading();
    try {
      if (await this.rejectTakenTitle(organizationId, model.name)) {
        return {started: false};
      }
      const result = await this.microreelsRepo.generateFromSlide(
        organizationId,
        model,
      );
      return this.resolveGenerationResult(result);
    } finally {
      this.loadingModule.endLoading();
    }
  }

  public async generateFromVideo(
    organizationId: string,
    model: MicroreelVideoGenerationModel,
  ) {
    this.loadingModule.startLoading();
    try {
      if (await this.rejectTakenTitle(organizationId, model.name)) {
        return {started: false};
      }
      const result = await this.microreelsRepo.generateFromVideo(
        organizationId,
        model,
      );
      return this.resolveGenerationResult(result);
    } finally {
      this.loadingModule.endLoading();
    }
  }

  // Suggests a group title from the source file name: the name without its
  // extension, and a " 2"/" 3"... suffix when that title is already used by
  // an existing group or an in-flight generation.
  public async getAvailableGroupTitle(
    organizationId: string,
    fileName: string,
  ): Promise<string> {
    const base = fileName
      .replace(/\.[^./\\]+$/, '')
      .trim()
      .slice(0, MAX_GROUP_TITLE_LENGTH)
      .trim();
    if (!base) return '';
    const taken = new Set(
      readGenerationInfos(organizationId).map(info =>
        normalizeGroupTitle(info.title),
      ),
    );
    const list = await this.microreelsRepo.getMicroreelsList(
      organizationId,
      0,
      TITLE_CHECK_PAGE_SIZE,
      base,
    );
    if (list.success) {
      (list.data?.data ?? []).forEach(group =>
        taken.add(normalizeGroupTitle(group.title)),
      );
    }
    if (!taken.has(normalizeGroupTitle(base))) return base;
    for (let n = 2; n < 100; n++) {
      const candidate = suffixedGroupTitle(base, n);
      if (!taken.has(normalizeGroupTitle(candidate))) return candidate;
    }
    return base;
  }

  // Duplicate titles are rejected against both the existing groups (searched
  // server-side, compared with the backend's 40-char truncation) and the
  // generations still in flight. Best-effort: a failed list fetch does not
  // block generating.
  private async rejectTakenTitle(organizationId: string, title?: string) {
    const normalized = normalizeGroupTitle(title);
    if (!normalized) return false;
    let taken = readGenerationInfos(organizationId).some(
      info => normalizeGroupTitle(info.title) === normalized,
    );
    if (!taken) {
      const list = await this.microreelsRepo.getMicroreelsList(
        organizationId,
        0,
        TITLE_CHECK_PAGE_SIZE,
        (title ?? '').trim(),
      );
      taken =
        !!list.success &&
        (list.data?.data ?? []).some(
          group => normalizeGroupTitle(group.title) === normalized,
        );
    }
    if (taken) {
      toast.error(
        getErrorFromCode(serverErrorCodes.MICROREEL_NAME_TAKEN),
        toastConfig,
      );
    }
    return taken;
  }

  // The AI job is kicked off before the response, so a lost response still
  // means the generation started. The generating page settles it.
  private resolveGenerationResult(result: {
    success: boolean;
    errors?: string[];
    data?: any;
  }): GenerationStartResult {
    if (result.success) {
      toast.success(generateMicroreelText.generationStarted, toastConfig);
      return {started: true, generationId: result.data?.generationId};
    }
    const code = result.errors?.[0];
    if (code === 'timeout' || code === 'unknown') {
      toast.success(generateMicroreelText.generationStarted, toastConfig);
      return {started: true};
    }
    if (code) {
      toast.error(getErrorFromCode(code), toastConfig);
    }
    return {started: false};
  }

  public async getGenerationStatus(
    organizationId: string,
    generationId?: string,
  ): Promise<MicroreelsGenerationStatusResult | undefined> {
    const result = await this.microreelsRepo.getGenerationStatus(
      organizationId,
      generationId,
    );
    return result.success ? result.data : undefined;
  }

  public async checkGenerationAvailability(
    organizationId: string,
  ): Promise<boolean> {
    const result =
      await this.microreelsRepo.checkGenerationAvailability(organizationId);
    if (!result.success && result.errors && result.errors[0]) {
      toast.error(getErrorFromCode(result.errors[0]), toastConfig);
      return false;
    }
    if (result.success) {
      return result.data ?? false;
    }
    return false;
  }

  public async cancelGeneration(organizationId: string) {
    const result = await this.microreelsRepo.cancelGeneration(organizationId);
    if (result.success) {
      toast.success(generateMicroreelText.generationCanceled, toastConfig);
    } else if (result.errors && result.errors[0]) {
      toast.error(getErrorFromCode(result.errors[0]), toastConfig);
    }
    return result.success;
  }

  // Bumped to cancel an in-flight source upload poll: the store is a
  // singleton, so without this the READY wait would keep the page-wide
  // loading flag on (and keep polling) after the create page unmounts.
  private uploadSession = 0;

  public abortSourceUpload() {
    this.uploadSession++;
    this.loadingModule.endLoading();
  }

  public async uploadSourceDocument(
    organizationId: string,
    file: File,
  ): Promise<MediaFile | undefined> {
    const session = ++this.uploadSession;
    this.loadingModule.startLoading();
    try {
      // generate_from_video refuses videos that are still transcoding, so the
      // upload waits for READY — the Upload button stays on "Uploading…"
      // meanwhile. On timeout the record is returned anyway and the backend
      // validation toast takes over.
      const result = await this.libraryRepo.uploadSourceFile(
        organizationId,
        file,
        {isCancelled: () => session !== this.uploadSession},
      );
      if (result.errors && result.errors[0]) {
        toast.error(getErrorFromCode(result.errors[0]), toastConfig);
      }
      if (!result.success || session !== this.uploadSession) {
        return undefined;
      }
      return result.record;
    } finally {
      if (session === this.uploadSession) {
        this.loadingModule.endLoading();
      }
    }
  }

  // Estimation is best-effort: failures fall back to the static estimates.
  public async getSlideCostEstimation(
    organizationId: string,
    model: VideoCostEstimationModel,
  ) {
    const result = await this.aiRepo.getVideoCostEstimation(
      organizationId,
      model,
    );
    return result.success && result.data ? result.data : undefined;
  }

  public async getPromptCostEstimation(
    organizationId: string,
    prompt: string,
    language: string,
  ) {
    const result = await this.microlessonsRepo.getMicrolessonCostEstimation(
      organizationId,
      {prompt, language},
    );
    return result.success && result.data ? result.data : undefined;
  }

  public async getLatestGroupId(
    organizationId: string,
  ): Promise<string | undefined> {
    const list = await this.microreelsRepo.getMicroreelsList(
      organizationId,
      0,
      1,
    );
    return list.success ? list.data?.data?.[0]?.id : undefined;
  }

  // The slide flow has no prompt field, so the "teach" text becomes the
  // description of the finished group.
  public async settleGenerations(
    organizationId: string,
  ): Promise<GenerationMatchResult> {
    const infos = readGenerationInfos(organizationId);
    if (!infos.length) return {finished: [], failed: [], pending: []};
    const list = infos.some(info => !info.generationId)
      ? await this.microreelsRepo.getMicroreelsList(
          organizationId,
          0,
          Math.max(SETTLE_PAGE_SIZE, infos.length * 2),
        )
      : undefined;
    if (list && !list.success)
      return {finished: [], failed: [], pending: infos, checkFailed: true};
    const result = matchGenerations(infos, list?.data?.data ?? [], Date.now());
    // The matcher can only call a timeout, and a slow generation looks exactly
    // like a dead one, so timed-out entries are re-checked against
    // generation_status before anyone is told the generation failed.
    const timedOut = new Set(result.failed.map(info => info.id));
    const toCheck = [...result.pending, ...result.failed];
    let checkFailed = false;
    if (toCheck.length) {
      const stillPending: StoredGenerationInfo[] = [];
      result.failed = [];
      for (const info of toCheck) {
        // Only ever ask about a known job id. Without one generation_status
        // answers about the requester's most recently updated job of any kind,
        // and a generation that lost its id did so because the start request
        // was aborted — at which point its own job row is not committed yet
        // (the first SaveAsync sits after the AI call), so the answer would
        // describe the previous generation and settle this one onto its group.
        const status = info.generationId
          ? await this.getGenerationStatus(organizationId, info.generationId)
          : undefined;
        const unverified = !!info.generationId && !status;
        if (unverified) checkFailed = true;
        const completedId =
          status?.status === MicroreelsGenerationStatus.Completed
            ? status.microreelsId
            : undefined;
        if (completedId) {
          result.finished.push({info, groupId: completedId});
        } else if (status?.status === MicroreelsGenerationStatus.Failed) {
          result.failed.push(info);
        } else if (timedOut.has(info.id) && !unverified) {
          result.failed.push(info);
        } else {
          stillPending.push(info);
        }
      }
      result.pending = stillPending;
    }
    result.checkFailed = checkFailed;
    for (const {info, groupId} of result.finished) {
      if (info.sourceFileName && info.description) {
        await this.applyDescriptionToGroup(
          organizationId,
          groupId,
          info.description,
        );
      }
      storeGroupSettings(groupId, info);
      storeGenerationResult(organizationId, {id: info.id, groupId});
      removeGenerationInfo(organizationId, info.id);
    }
    result.failed.forEach(info => {
      storeGenerationResult(organizationId, {id: info.id, failed: true});
      removeGenerationInfo(organizationId, info.id);
    });
    return result;
  }

  private async applyDescriptionToGroup(
    organizationId: string,
    groupId: string,
    description: string,
  ) {
    const courseResult = await this.coursesRepo.getCourse(
      organizationId,
      groupId,
    );
    if (courseResult.success && courseResult.data) {
      await this.coursesRepo.editCourse(organizationId, {
        ...courseResult.data,
        description,
      });
    }
  }
}

export default GenerateMicroreelStore;
